import { Alert, AlertDescription } from '@brent/core-ui/ui'
import { useActiveSurvey, useMyResponse, useSubmitResponse } from '@/hooks/useSurveys'
import SurveyForm from '@/components/features/surveys/SurveyForm'
import MembersOnlyNotice from '@/components/shared/MembersOnlyNotice'
import { useAuth } from '@/hooks/useAuth'

export default function Survey() {
  const { hasGroup } = useAuth()
  const isMember = hasGroup('member')
  const {
    data: survey,
    isLoading: surveyLoading,
    error: surveyError,
  } = useActiveSurvey(isMember)
  const { data: myResponse } = useMyResponse(survey?.survey_id)
  const submitMutation = useSubmitResponse()

  if (!isMember) {
    return <MembersOnlyNotice title="Survey" />
  }

  const handleSubmit = (likertAnswers: Record<string, number>, freeTextAnswer: string) => {
    if (!survey) return
    submitMutation.mutate({ surveyId: survey.survey_id, likertAnswers, freeTextAnswer })
  }

  if (surveyLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl sm:text-3xl font-bold">Survey</h1>
        <div className="h-48 animate-pulse rounded-lg bg-muted" />
      </div>
    )
  }

  if (surveyError || !survey) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl sm:text-3xl font-bold">Survey</h1>
        <Alert>
          <AlertDescription>
            There is no active survey at the moment. Check back later!
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl sm:text-3xl font-bold">Survey</h1>

      {submitMutation.isSuccess && (
        <Alert>
          <AlertDescription>
            Your response has been {myResponse ? 'updated' : 'submitted'} successfully. You can
            update your answers until the survey closes.
          </AlertDescription>
        </Alert>
      )}

      {submitMutation.isError && (
        <Alert variant="destructive">
          <AlertDescription>
            {submitMutation.error instanceof Error
              ? submitMutation.error.message
              : 'Failed to submit response. Please try again.'}
          </AlertDescription>
        </Alert>
      )}

      <SurveyForm
        survey={survey}
        currentAnswers={
          myResponse
            ? {
                likert_answers: myResponse.likert_answers,
                free_text_answer: myResponse.free_text_answer,
              }
            : undefined
        }
        isPending={submitMutation.isPending}
        onSubmit={handleSubmit}
      />
    </div>
  )
}
